import { Dayjs } from "dayjs";
import dayjs from "dayjs";
import { useMemo, useCallback } from "react";
import { useGetUnverifiedDatesQuery } from "src/store/api/generatedApi";
import { useTenant } from "hooks/settings";
import { useScrapEyeMetadata } from "contexts/scrapEyeMetadata";

interface UseUnverifiedDateNavigationProps {
  location: string | undefined;
  selectedDate: Dayjs;
  setSelectedDate: (date: Dayjs) => void;
}

export const useUnverifiedDateNavigation = ({
  location,
  selectedDate,
  setSelectedDate,
}: UseUnverifiedDateNavigationProps) => {
  const tenantName = useTenant();
  const { reservation: reservationConfig } = useScrapEyeMetadata();

  const { data: unverifiedDates, isLoading } = useGetUnverifiedDatesQuery(
    {
      tenantName,
      location: location ?? "",
    },
    {
      skip: !location,
    }
  );

  const reserveFromDate = useMemo(
    () =>
      reservationConfig.default_reserve_from
        ? dayjs.utc(reservationConfig.default_reserve_from)
        : null,
    [reservationConfig.default_reserve_from]
  );

  const sortedDates = useMemo(
    () =>
      (unverifiedDates ?? [])
        .map((d) => dayjs.utc(d))
        .filter((d) => !reserveFromDate || !d.isBefore(reserveFromDate, "day"))
        .sort((a, b) => a.valueOf() - b.valueOf()),
    [unverifiedDates, reserveFromDate]
  );

  const currentDateStr = selectedDate.format("YYYY-MM-DD");

  const hasPrevUnverified = useMemo(() => {
    if (sortedDates.length === 0) return false;
    return sortedDates.some((d) => d.format("YYYY-MM-DD") < currentDateStr);
  }, [sortedDates, currentDateStr]);

  const hasNextUnverified = useMemo(() => {
    if (sortedDates.length === 0) return false;
    return sortedDates.some((d) => d.format("YYYY-MM-DD") > currentDateStr);
  }, [sortedDates, currentDateStr]);

  const goToPrevUnverified = useCallback(() => {
    const prevDates = sortedDates.filter(
      (d) => d.format("YYYY-MM-DD") < currentDateStr
    );
    const prevDate = prevDates[prevDates.length - 1];
    if (prevDate) {
      setSelectedDate(prevDate);
    }
  }, [sortedDates, currentDateStr, setSelectedDate]);

  const goToNextUnverified = useCallback(() => {
    const nextDates = sortedDates.filter(
      (d) => d.format("YYYY-MM-DD") > currentDateStr
    );
    const nextDate = nextDates[0];
    if (nextDate) {
      setSelectedDate(nextDate);
    }
  }, [sortedDates, currentDateStr, setSelectedDate]);

  return {
    unverifiedDates: sortedDates.map((d) => d.toISOString()),
    isLoading,
    hasPrevUnverified,
    hasNextUnverified,
    goToPrevUnverified,
    goToNextUnverified,
    totalUnverifiedDates: sortedDates.length,
  };
};
